import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const TourContext = createContext();

export const useTour = () => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

export const TourProvider = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tourData, setTourData] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [shouldShowTour, setShouldShowTour] = useState(false);

  // Check if user has completed tour or disabled it
  useEffect(() => {
    const tourCompleted = localStorage.getItem('crm-tour-completed');
    const tourDisabled = localStorage.getItem('crm-tour-disabled');
    const isFirstVisit = !localStorage.getItem('crm-first-visit');

    if (isFirstVisit) {
      localStorage.setItem('crm-first-visit', 'true');
      setShouldShowTour(true);
    } else if (!tourCompleted && !tourDisabled) {
      setShouldShowTour(true);
    }
  }, []);

  const startTour = useCallback((steps) => {
    setTourData(steps);
    setCurrentStep(0);
    setIsActive(true);
    setIsCompleted(false);
  }, []);

  const nextStep = useCallback(() => {
    if (tourData && currentStep < tourData.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeTour();
    }
  }, [currentStep, tourData]);

  const previousStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const skipTour = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    setTourData(null);
    localStorage.setItem('crm-tour-disabled', 'true');
  }, []);

  const completeTour = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    setTourData(null);
    setIsCompleted(true);
    localStorage.setItem('crm-tour-completed', 'true');
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem('crm-tour-completed');
    localStorage.removeItem('crm-tour-disabled');
    setShouldShowTour(true);
    setIsCompleted(false);
  }, []);

  const getCurrentStepData = useCallback(() => {
    if (!tourData || currentStep >= tourData.length) return null;
    return tourData[currentStep];
  }, [tourData, currentStep]);

  const value = {
    isActive,
    currentStep,
    tourData,
    isCompleted,
    shouldShowTour,
    startTour,
    nextStep,
    previousStep,
    skipTour,
    completeTour,
    resetTour,
    getCurrentStepData,
    setIsActive,
    setCurrentStep,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};
